import React, { useState } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ventures, ongoingInitiatives, stats } from '../data/mock';
import { ArrowRight } from 'lucide-react';

function VentureCard({ venture, index }) {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`bg-[#0d0d0d] border border-white/5 rounded-2xl p-6 md:p-8 flex flex-col justify-between min-h-[320px] group hover:border-emerald-500/20 transition-all duration-500 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div>
        <div className="flex items-center justify-between mb-6">
          <span className="font-mono text-[10px] tracking-[0.2em] text-white/20">
            {venture.year || '2025'}
          </span>
          {venture.status && (
            <span
              className="font-mono text-[10px] tracking-[0.15em] px-3 py-1 rounded-full"
              style={{
                color: venture.statusColor,
                border: `1px solid ${venture.statusColor}30`,
                backgroundColor: `${venture.statusColor}10`,
              }}
            >
              {venture.status}
            </span>
          )}
        </div>

        <h3 className="text-xl md:text-2xl font-bold text-white mb-3 tracking-[-0.01em] group-hover:text-emerald-400 transition-colors duration-300">
          {venture.name}
        </h3>
        <p className="font-mono text-xs leading-relaxed text-white/35 mb-6">
          {venture.description}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {venture.tags.map((tag) => (
          <span
            key={tag}
            className="font-mono text-[10px] tracking-[0.1em] px-3 py-1.5 rounded-full border border-white/8 text-white/30 group-hover:border-emerald-500/20 group-hover:text-white/50 transition-colors"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function ConceptCard() {
  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#0d0d0d] border border-emerald-500/10 rounded-2xl p-6 md:p-8 flex flex-col items-center justify-center min-h-[320px] text-center group hover:border-emerald-500/30 transition-all duration-500">
      <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-[-0.01em]">
        Have a<br />
        <span className="text-emerald-400">Concept?</span>
      </h3>
      <p className="font-mono text-xs text-white/30 mb-6 tracking-[0.1em]">
        We Build Scalable Solutions
      </p>
      <button
        onClick={scrollToContact}
        className="font-mono text-xs tracking-[0.15em] px-6 py-3 border border-emerald-500/30 rounded-full text-emerald-400 hover:bg-emerald-500 hover:text-black transition-all duration-300 flex items-center gap-2"
      >
        Partner With Us
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

export default function VenturesSection() {
  const [headerRef, headerVisible] = useScrollReveal({ threshold: 0.2 });
  const [showInitiatives, setShowInitiatives] = useState(false);

  return (
    <section id="work" className="bg-[#050505] py-20 md:py-32">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {/* Section Header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 transition-all duration-1000 ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div>
            <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/50 uppercase">
              Our Brands :: Portfolio
            </span>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mt-3 tracking-[-0.02em] text-white">
              Our <span className="text-white/20">iGaming Network</span>
            </h2>
            <p className="font-mono text-xs text-white/35 mt-4 max-w-lg leading-relaxed">
              We don't just promote. We build. These are the affiliate brands we own, operate, and scale. Each one engineered for player value and operator performance.
            </p>
          </div>

          {/* Stats */}
          <div className="flex gap-8 md:gap-12">
            <div className="text-right">
              <div className="text-2xl md:text-3xl font-black text-emerald-400">{stats.monthlyVisits}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] text-white/30 mt-1">Monthly Visits</div>
            </div>
            <div className="text-right">
              <div className="text-2xl md:text-3xl font-black text-emerald-400">{stats.activeNodes}</div>
              <div className="font-mono text-[10px] tracking-[0.15em] text-white/30 mt-1">Active Brands</div>
            </div>
            <div className="text-right">
              <div className="font-mono text-[10px] tracking-[0.15em] text-white/20">/// EST. {stats.established}</div>
            </div>
          </div>
        </div>

        {/* Ventures Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ventures.map((venture, i) => (
            <VentureCard key={venture.id} venture={venture} index={i} />
          ))}
          <ConceptCard />
        </div>

        {/* Ongoing Initiatives */}
        <div className="mt-8 bg-[#0d0d0d] border border-white/5 rounded-2xl overflow-hidden">
          <button
            onClick={() => setShowInitiatives(!showInitiatives)}
            className="w-full flex items-center justify-between px-6 md:px-8 py-5 group"
          >
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-mono text-xs tracking-[0.2em] text-white/50 uppercase">
                Ongoing Initiatives
              </span>
            </div>
            <div className="flex items-center gap-2 font-mono text-xs tracking-[0.15em] text-white/30 group-hover:text-emerald-400 transition-colors">
              [{showInitiatives ? '-' : '+'}] View List
            </div>
          </button>

          <div
            className={`overflow-hidden transition-all duration-500 ${
              showInitiatives ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="px-6 md:px-8 pb-6 space-y-3">
              {ongoingInitiatives.map((item) => (
                <div
                  key={item.name}
                  className="flex items-center justify-between py-3 border-t border-white/5"
                >
                  <span className="font-mono text-sm text-white/60">{item.name}</span>
                  <div className="flex items-center gap-4">
                    <span className="font-mono text-xs text-white/30">{item.type}</span>
                    <span className="font-mono text-xs text-white/20">{item.year}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
